import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';

import { SimpleCallback } from '@frontend/sports/common/core/utils/extended-types';
import { CustomWindow, WINDOW } from '@frontend/vanilla/core';
import { Observable, timer } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class TimerService implements OnDestroy {
    protected ngZone = inject(NgZone);
    protected _window = inject<CustomWindow>(WINDOW);

    private timeouts = new Map<number, SimpleCallback>();
    private intervals = new Map<number, SimpleCallback>();
    private frames = new Map<number, SimpleCallback>();

    setTimeout(handler: SimpleCallback, time?: number): SimpleCallback {
        return this.setCallback(handler, this.timeouts, (callback) => this._window.setTimeout(callback, time), this._window.clearTimeout);
    }

    setInterval(handler: SimpleCallback, time: number): SimpleCallback {
        return this.setCallback(handler, this.intervals, (callback) => this._window.setInterval(callback, time), this._window.clearInterval, true);
    }

    setAnimationFrame(handler: SimpleCallback): SimpleCallback {
        return this.setCallback(handler, this.frames, this._window.requestAnimationFrame, this._window.cancelAnimationFrame);
    }

    setTimeoutOutsideAngular(handler: SimpleCallback, time?: number): SimpleCallback {
        return this.ngZone.runOutsideAngular(() => this.setTimeout(handler, time));
    }

    setIntervalOutsideAngular(handler: SimpleCallback, time: number): SimpleCallback {
        return this.ngZone.runOutsideAngular(() => {
            return this.setInterval(handler, time);
        });
    }

    private setCallback(
        callback: SimpleCallback,
        map: Map<number, SimpleCallback>,
        scheduler: (callback: SimpleCallback) => number,
        canceller: (handle: number) => void,
        continuous = false,
    ): SimpleCallback {
        const val = scheduler(() => {
            callback();

            if (!continuous) {
                map.delete(val);
            }
        });

        const removeCallback = () => {
            canceller(val);
            map.delete(val);
        };

        map.set(val, removeCallback);

        return removeCallback;
    }

    timer(dueTime: number | Date = 0): Observable<number> {
        return timer(dueTime);
    }

    removeAll(): void {
        for (const collection of [this.timeouts, this.intervals, this.frames]) {
            for (const destructor of collection.values()) {
                destructor();
            }
        }
    }

    ngOnDestroy(): void {
        this.removeAll();
    }
}
